import React, { useContext } from 'react';
import styled from 'styled-components';
import {
  QBConnector,
  QBItemProps,
  QBItemSelection,
} from '@/components/query-builder/models';
import { Flexbox } from '@/components/flex';
import { QBRootValue } from '@/components/query-builder/root-value';
import {
  QBButton,
  QBCaption,
  QBVSpace,
} from '@/components/query-builder/shared';
import {
  createQBContext,
  QBContext,
  QBContextProps,
} from '@/components/query-builder/context';
import { OnboardingQueryBuilder } from '@/components/query-builder/onboarding';

type Props = {
  initialProps?: QBItemProps;
  initialState?: QBItemSelection[];
  onChange?: (value: QBItemSelection[]) => void;
  disableAndBtn?: boolean;
  noOnboarding?: boolean;
  whereNotion?: string;
  andNotion?: string;
  textCondNoMarginTop?: boolean;
  selectMaxWidth?: number;
  noCaption?: boolean;
  plusNotion?: string;
  context?: QBContextProps;
  andOrEnabled?: boolean;
};

export function QueryBuilder({
  initialProps,
  initialState,
  onChange,
  disableAndBtn,
  whereNotion,
  textCondNoMarginTop,
  selectMaxWidth,
  noOnboarding,
  noCaption,
  andNotion,
  plusNotion,
  context,
  andOrEnabled,
}: Props) {
  const ctx = context || createQBContext(initialState, initialProps, onChange);
  const { appendKey, setConnector, queryState } = ctx;

  const globalConnector: QBConnector | null =
    queryState.find((q, i) => i > 0 && q.connector != null)?.connector ?? null;

  const connectorLocked = globalConnector != null;

  const onAddConnectorRow = (connector: QBConnector) => {
    setConnector(connector);
    appendKey(true, connector);
  };

  const hasExistingConnectors = queryState.some(
    (q, i) => i > 0 && q.connector != null,
  );
  const isLegacyAndOnly = queryState.length > 1 && !hasExistingConnectors;

  const showAndButton =
    !disableAndBtn &&
    (!andOrEnabled || !connectorLocked || globalConnector === `and`);
  const showOrButton =
    !disableAndBtn &&
    andOrEnabled &&
    !isLegacyAndOnly &&
    (!connectorLocked || globalConnector === `or`);

  return (
    <>
      {!noOnboarding && <OnboardingQueryBuilder obsRef={ctx.onboardingRef} />}
      <QBContext.Provider value={ctx}>
        <Wrapper singleRow={textCondNoMarginTop}>
          <QueryBuilderInner
            andNotion={andNotion}
            noCaption={noCaption}
            selectMaxWidth={selectMaxWidth}
            whereNotion={whereNotion}
            textCondNoMarginTop={textCondNoMarginTop}
            andOrEnabled={andOrEnabled}
          />
          <ButtonsRow>
            {showAndButton && (
              <AndButton onClick={() => onAddConnectorRow(`and`)}>
                {plusNotion || `+ And`}
              </AndButton>
            )}
            {showOrButton && (
              <OrButton onClick={() => onAddConnectorRow(`or`)}>|| Or</OrButton>
            )}
          </ButtonsRow>
          <QBVSpace />
        </Wrapper>
      </QBContext.Provider>
    </>
  );
}

function QueryBuilderInner({
  whereNotion,
  andNotion,
  textCondNoMarginTop,
  selectMaxWidth,
  noCaption,
  andOrEnabled,
}: {
  whereNotion?: string;
  andNotion?: string;
  textCondNoMarginTop?: boolean;
  selectMaxWidth?: number;
  noCaption?: boolean;
  andOrEnabled?: boolean;
}) {
  const {
    queryState,
    firstOptions,
    onboardingRef: [, refSetter],
  } = useContext(QBContext);

  function getConnectorLabel(
    andOrEnabled: boolean | undefined,
    connector: QBConnector | null | undefined,
    andNotion: string | undefined,
  ): string {
    if (!andOrEnabled) {
      return andNotion || `And`;
    }
    return (connector ?? `and`) === `or` ? `Or` : `And`;
  }

  return (
    <>
      {queryState.map((q, idx) => (
        <React.Fragment key={`query-${q?.qbProps?.caption}${idx}`}>
          <QBVSpace />
          <Row ref={refSetter} noContent={!whereNotion && idx === 0}>
            {idx === 0 ? (
              <Where>{whereNotion || ``}</Where>
            ) : (
              <LogicalAnd>
                {getConnectorLabel(andOrEnabled, q?.connector, andNotion)}
              </LogicalAnd>
            )}
            <QBRootValue
              noCaption={noCaption}
              idx={idx}
              options={firstOptions()}
              selectMaxWidth={selectMaxWidth}
              textCondNoMarginTop={textCondNoMarginTop}
            />
          </Row>
        </React.Fragment>
      ))}
    </>
  );
}

const Wrapper = styled(Flexbox)`
  && {
    height: 100%;
    ${({ singleRow }: { singleRow?: boolean }) =>
      singleRow ? `` : `overflow-y: scroll;`}
    ${({ singleRow }: { singleRow?: boolean }) =>
      singleRow ? `` : `overflow-x: hidden;`}


    flex-direction: ${({ singleRow }: { singleRow?: boolean }) =>
      singleRow ? `row` : `column`};
    flex-wrap: nowrap;

    align-items: flex-start;
    justify-content: flex-start;

    /* Dont Remove, it fixes the box shadow of the AND button being cut off */
    margin: -10px;
    padding: 10px;
    /* Dont Remove, it fixes the box shadow of the AND button being cut off */
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: ${(p: any) => (p?.noContent ? `0` : `3rem`)} 1fr;

  grid-gap: ${(p: any) => (p?.noContent ? `0` : `2rem`)};
  align-items: flex-start;
  margin-bottom: 2rem;
  width: 100%;
`;

const LogicalAnd = styled(QBCaption)`
  && {
    margin-top: 0.65rem;
  }
`;

const AndButton = styled(QBButton)`
  && {
    width: 15rem;
  }
`;

const OrButton = styled(QBButton)`
  && {
    width: 15rem;
  }
`;

const ButtonsRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
`;

const Where = styled(QBCaption)`
  && {
    margin-top: 0.65rem;
    margin-right: 1rem;
  }
`;
